// MO7.1, MO7.1M
import type { FC } from 'react';

import { useCallback, useContext, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { sanitizeUrl } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { MeasuredCookie } from '@wix/da-hooks/pkg/useMeasureElement/redux/MeasuredCookie';
import { useHeroImageForContainer } from '@wix/da-shared-react/pkg/utils/hooks/useHeroImageForContainer';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, SearchClickBiEvent } from '@wix/da-bi/pkg/events';
import SearchBar from './SearchBar';

import s from './ShopPageHeader.scss';

/** which tab are we automatically directed to when searching from the /shop page */
const DEFAULT_TAB_ON_SEARCH = 'exclusives';

export interface Props {
  searchTerm?: string;
  bgDeviation?: PapiDeviation;
}

/** shop page explanation text and search input, sits above subnav tabs */
const ShopPageHeader: FC<Props> = ({ searchTerm = '', bgDeviation }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const logBiEvent = useBiLogger();

  const isFirstVisit = useRef<boolean>(!MeasuredCookie.hasCookie());
  const rootDivRef = useRef<HTMLDivElement>(null);
  const { hero } = useHeroImageForContainer(bgDeviation, rootDivRef, {
    isViewportWidth: true,
    width: 1024,
    height: 215,
    mobileWidth: 412,
    mobileHeight: 215,
  });
  const shouldUseBgImage =
    !!hero &&
    !searchTerm &&
    isFirstVisit.current === false;
  const backgroundImage = shouldUseBgImage ? `url(${hero.src})` : undefined;

  const submitCallback = useCallback(
    newSearchTerm => {
      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: newSearchTerm,
          sectionname: 'shop',
        })
      );

      const nextUrl = sanitizeUrl(
        newSearchTerm
          ? getPathnameWithSearch(newSearchTerm)
          : getPathnameWithoutSearch()
      );
      nextUrl && window.location.assign(nextUrl);
    },
    [logBiEvent]
  );

  return (
    (<header
      className={classnames(
        s['root'],
        searchTerm && s['with-search'],
        backgroundImage && s['with-custom-bg']
      )}
      style={{ backgroundImage }}
    >
      {shouldUseBgImage && (
        /* lets the preloader scan it */
        (<img
          src={hero.src}
          alt=""
          width="0"
          height="0"
          style={{ display: 'none' }}
        />)
      )}
      {!searchTerm && (
        <h1 className={s['title']}>{t('shopPage.header.title')}</h1>
      )}
      <SearchBar
        className={s['search-bar']}
        id="shop-search-bar"
        name="q"
        placeholder={
          isMobile
            ? t('shopPage.header.searchFieldPlaceholder.mobile')
            : t('shopPage.header.searchFieldPlaceholder')
        }
        initialValue={searchTerm}
        buttonText={t('shopPage.header.searchButton')}
        onSubmit={submitCallback}
      />
    </header>)
  );
};

ShopPageHeader.displayName = 'ShopPageHeader';
export default ShopPageHeader;

// these are to handle the special /shop/search1=term vs /shop
// others: /shop/commissions/search1=term vs. /shop/commissions
function getPathnameWithoutSearch(): string {
  if (typeof window === 'undefined') {
    return '/shop';
  }
  const { pathname: windowPathname, search } = window.location;
  const newPathname =
    windowPathname === '/shop/search'
      ? '/shop'
      : windowPathname.replace('/search', '');

  const params = new URLSearchParams(search);
  params.delete('q');
  const newLocationSearch = params.toString();
  return newLocationSearch
    ? `${newPathname}?${params.toString()}`
    : newPathname;
}

function getPathnameWithSearch(searchTerm: string): string {
  if (typeof window === 'undefined') {
    return '/shop/search';
  }
  const { pathname: windowPathname, search } = window.location;

  let newPathname: string;
  if (windowPathname.endsWith('/search')) {
    newPathname = windowPathname;
  } else if (windowPathname === '/shop') {
    newPathname = `${windowPathname}/${DEFAULT_TAB_ON_SEARCH}/search`;
  } else {
    newPathname = `${windowPathname}/search`;
  }

  const params = new URLSearchParams(search);
  params.delete('q');
  params.append('q', searchTerm);
  return `${newPathname}?${params.toString()}`;
}
